/**
 * @file api.ts
 * @author Arunkumar Annamalai
 * @version 1.0.0
 * @description API service configuration and endpoints for the Shay Suite Admin frontend.
 *              This file sets up axios instance with interceptors, handles authentication,
 *              manages request/response formatting, and provides all API endpoint methods.
 *              It includes data transformation utilities and error handling.
 * @created Aug-2025
 * @lastModified Aug-2025
 */

import axios from 'axios';
import { API_CONFIG, API_ENDPOINTS, APP_CONFIG, STORAGE_KEYS, clearAllAuthData } from '../helpers/constants';

/**
 * Axios instance with base configuration
 * Provides centralized HTTP client with default settings
 */
const api = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor to add authentication token
 * Automatically includes JWT token in Authorization header for authenticated requests
 */
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor to handle standardized response format and common errors
 * 
 * This interceptor:
 * - Processes standardized API responses (code, data, message format)
 * - Extracts data field from successful responses
 * - Handles error responses with appropriate error messages
 * - Manages authentication errors with automatic token refresh
 * - Provides consistent error handling across the application
 */
api.interceptors.response.use(
  (response) => {
    // Handle standardized response format
    if (response.data && response.data.hasOwnProperty('code') && response.data.hasOwnProperty('data')) {
      // Check if it's a success response
      if (response.data.code.startsWith('S')) {
        // Return the data field for successful responses
        response.data = response.data.data;
      } else {
        // For error responses, throw an error with the message
        throw new Error(response.data.message || 'An error occurred');
      }
    }
    // If response doesn't have the standardized format, return as is
    return response;
  },
  async (error) => {
    // Handle standardized error responses
    if (error.response?.data) {
      // Check if it's a standardized error response
      if (error.response.data.hasOwnProperty('code') && error.response.data.hasOwnProperty('message')) {
        const errorMessage = error.response.data.message || 'An error occurred';
        error.message = errorMessage;
      } else if (error.response.data.hasOwnProperty('message')) {
        // Handle simple error responses like {message: "Invalid or expired token"}
        const errorMessage = error.response.data.message || 'An error occurred';
        error.message = errorMessage;
      } else {
        // Handle other error formats
        error.message = error.response.data || 'An error occurred';
      }
    }
    
    if (error.response?.status === 401) {
      // Try to refresh token before redirecting to logout
      const refreshToken = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
      if (refreshToken) {
        try {
          const response = await fetch(`${API_CONFIG.BASE_URL}/auth/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
          });

          if (response.ok) {
            const data = await response.json();
            if (data.code && data.code.startsWith('S')) {
              const { token: newToken, refreshToken: newRefreshToken } = data.data;
              localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, newToken);
              localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, newRefreshToken);
              
              // Retry the original request with new token
              const originalRequest = error.config;
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return api(originalRequest);
            }
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
        }
      }
      
      // If refresh fails or no refresh token, logout
      clearAllAuthData();
      window.location.href = '/logout';
    }
    return Promise.reject(error);
  }
);

/**
 * Admin API endpoints object
 * Provides all API methods for admin functionality
 */
export const adminAPI = {
  /**
   * Authenticate user with email and password
   * 
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email - User's email address
   * @param {string} credentials.password - User's password
   * @returns {Promise} API response with token and user data
   */
  login: (credentials: { email: string; password: string }) => 
    api.post(API_ENDPOINTS.AUTH.LOGIN, credentials),
  
  /**
   * Get all companies
   * 
   * @returns {Promise} API response with companies list
   */
  getCompanies: () => api.get(API_ENDPOINTS.ADMIN.COMPANIES),
  
  /**
   * Get a single company by ID
   * 
   * @param {string} id - Company ID
   * @returns {Promise} API response with company data
   */
  getCompany: (id: string) => api.get(API_ENDPOINTS.ADMIN.COMPANY_BY_ID(id)),
  
  /**
   * Get first admin user email for a company
   * 
   * @param {string} id - Company ID
   * @returns {Promise} API response with admin user email
   */
  getCompanyAdminUser: (id: string) => api.get(API_ENDPOINTS.ADMIN.COMPANY_ADMIN_USER(id)),

  /**
   * Get all users for a company
   *
   * @param {string} id - Company ID
   * @returns {Promise} API response with list of users (name, email, role)
   */
  getCompanyUsers: (id: string) => api.get(API_ENDPOINTS.ADMIN.COMPANY_USERS(id)),

  /**
   * Create a new company
   * 
   * @param {Object} data - Company data
   * @returns {Promise} API response with created company
   */
  

  /**
   * Update a company
   * 
   * @param {string} id - Company ID
   * @param {Object} data - Updated company data
   * @returns {Promise} API response with updated company
   */
  updateCompany: (id: string, data: any) => {
    // Convert snake_case to camelCase for backend
    const camelCaseData = {
      name: data.name,
      domain: data.domain,
      contactEmail: data.contactEmail || data.contact_email,
      industry: data.industry,
      status: data.status
    };
    return api.put(API_ENDPOINTS.ADMIN.UPDATE_COMPANY(id), camelCaseData);
  },
  
  /**
   * Delete a company
   * 
   * @param {string} id - Company ID
   * @returns {Promise} API response
   */
  deleteCompany: (id: string) => api.delete(API_ENDPOINTS.ADMIN.DELETE_COMPANY(id)),
  
  /**
   * Get company subscription
   * 
   * @param {string} id - Company ID
   * @returns {Promise} API response with subscription data
   */
  getCompanySubscription: (id: string) => api.get(API_ENDPOINTS.ADMIN.COMPANY_SUBSCRIPTION(id)),
  
  /**
   * Get all subscription plans
   * 
   * @returns {Promise} API response with plans list
   */
  getPlans: () => api.get(API_ENDPOINTS.ADMIN.PLANS, { params: { platform_name: APP_CONFIG.PLATFORM_NAME } }),
  
  /**
   * Get public plans (for onboarding)
   * 
   * @returns {Promise} API response with public plans
   */
  getPublicPlans: () => api.get(API_ENDPOINTS.PUBLIC.PLANS),
  
  /**
   * Get a single plan by ID
   * 
   * @param {string} id - Plan ID
   * @returns {Promise} API response with plan data
   */
  getPlan: (id: string) => api.get(API_ENDPOINTS.ADMIN.PLAN_BY_ID(id)),
  
  /**
   * Create a new subscription plan
   * 
   * @param {Object} data - Plan data
   * @returns {Promise} API response with created plan
   */
  createPlan: (data: any) => {
    // Convert to camelCase for backend
    const camelCaseData = {
      name: data.name,
      planCode: data.planCode || data.plan_code,
      description: data.description,
      monthlyPrice: data.monthlyPrice !== undefined ? data.monthlyPrice : (data.monthly_price !== undefined ? data.monthly_price : 0),
      planCycles: data.planCycles !== undefined ? data.planCycles : (data.plan_cycles !== undefined ? data.plan_cycles : -1),
      features: data.features,
      isActive: data.isActive !== undefined ? data.isActive : (data.is_active !== undefined ? data.is_active : true),
      interval: data.interval !== undefined ? data.interval : 1,
      intervalUnit: data.intervalUnit || data.interval_unit || 'months',
      platform_name: data.platform_name || APP_CONFIG.PLATFORM_NAME
    };
    console.log('Sending createPlan data:', camelCaseData);
    console.log('API endpoint:', API_ENDPOINTS.ADMIN.CREATE_PLAN);
    console.log('Making POST request to:', `${API_CONFIG.BASE_URL}${API_ENDPOINTS.ADMIN.CREATE_PLAN}`);
    return api.post(API_ENDPOINTS.ADMIN.CREATE_PLAN, camelCaseData);
  },
  
  /**
   * Update a subscription plan
   * 
   * @param {string} id - Plan ID
   * @param {Object} data - Updated plan data
   * @returns {Promise} API response with updated plan
   */
  updatePlan: (id: string, data: any) => {
    // Convert to camelCase for backend
    const camelCaseData = {
      name: data.name,
      planCode: data.planCode || data.plan_code,
      description: data.description,
      monthlyPrice: data.monthlyPrice !== undefined ? data.monthlyPrice : (data.monthly_price !== undefined ? data.monthly_price : 0),
      planCycles: data.planCycles !== undefined ? data.planCycles : (data.plan_cycles !== undefined ? data.plan_cycles : -1),
      features: data.features,
      isActive: data.isActive !== undefined ? data.isActive : (data.is_active !== undefined ? data.is_active : true),
      interval: data.interval !== undefined ? data.interval : 1,
      intervalUnit: data.intervalUnit || data.interval_unit || 'months',
      platform_name: data.platform_name || APP_CONFIG.PLATFORM_NAME
    };
    return api.put(API_ENDPOINTS.ADMIN.UPDATE_PLAN(id), camelCaseData);
  },
  
  /**
   * Delete a subscription plan
   * 
   * @param {string} id - Plan ID
   * @param {Object} params - Query parameters
   * @param {string} params.platform_name - Platform name
   * @returns {Promise} API response
   */
  deletePlan: (id: string, params?: { platform_name?: string }) => {
    const queryParams = params?.platform_name ? { platform_name: params.platform_name } : {};
    return api.delete(API_ENDPOINTS.ADMIN.DELETE_PLAN(id), { params: queryParams });
  },
  
  /**
   * Get all API keys
   * 
   * @returns {Promise} API response with API keys list
   */
  getApiKeys: () => api.get(API_ENDPOINTS.ADMIN.API_KEYS),
  
  /**
   * Get API keys for a specific company
   * 
   * @param {string} companyId - Company ID
   * @returns {Promise} API response with company API keys
   */
  getApiKeysByCompany: (companyId: string) => api.get(API_ENDPOINTS.ADMIN.API_KEYS_BY_COMPANY(companyId)),
  
  /**
   * Get a single API key by ID
   * 
   * @param {string} id - API key ID
   * @returns {Promise} API response with API key data
   */
  getApiKey: (id: string) => api.get(API_ENDPOINTS.ADMIN.API_KEY_BY_ID(id)),
  
  /**
   * Generate new API key
   * 
   * @param {Object} data - API key data
   * @returns {Promise} API response with generated API key
   */
  generateApiKey: (data: any) => {
    // Convert to snake_case for backend
    const snakeCaseData = {
      company_id: data.companyId || data.company_id,
      name: data.name,
      description: data.description,
      is_active: data.isActive || data.is_active
    };
    return api.post(API_ENDPOINTS.ADMIN.CREATE_API_KEY, snakeCaseData);
  },
  
  /**
   * Update API key
   * 
   * @param {string} id - API key ID
   * @param {Object} data - Updated API key data
   * @returns {Promise} API response with updated API key
   */
  updateApiKey: (id: string, data: any) => {
    // Convert to snake_case for backend
    const snakeCaseData = {
      name: data.name,
      description: data.description,
      is_active: data.isActive || data.is_active
    };
    return api.put(API_ENDPOINTS.ADMIN.UPDATE_API_KEY(id), snakeCaseData);
  },
  
  /**
   * Update API key status
   * 
   * @param {string} id - API key ID
   * @param {boolean} isActive - Active status
   * @returns {Promise} API response
   */
  updateApiKeyStatus: (id: string, isActive: boolean) => 
    api.put(API_ENDPOINTS.ADMIN.UPDATE_API_KEY_STATUS(id), { is_active: isActive }),
  
  /**
   * Regenerate API key
   * 
   * @param {string} id - API key ID
   * @returns {Promise} API response with new API key
   */
  regenerateApiKey: (id: string) => api.post(API_ENDPOINTS.ADMIN.REGENERATE_API_KEY(id)),
  
  /**
   * Get usage summary
   * 
   * @param {Object} params - Query parameters
   * @returns {Promise} API response with usage summary
   */
  getUsageSummary: (params?: any) => api.get(API_ENDPOINTS.ADMIN.USAGE_SUMMARY, { params }),
  
  /**
   * Get company usage
   * 
   * @param {string} companyId - Company ID
   * @returns {Promise} API response with company usage data
   */
  getCompanyUsage: (companyId: string) => api.get(API_ENDPOINTS.ADMIN.COMPANY_USAGE(companyId)),
  
  /**
   * Send welcome email
   * 
   * @param {Object} data - Email data
   * @returns {Promise} API response
   */
  sendWelcomeEmail: (data: { customerId: string; contactEmail: string; planId?: string }) => 
    api.post(API_ENDPOINTS.ADMIN.SEND_WELCOME_EMAIL, data),
  
  
  /**
   * Create customer
   * 
   * @param {Object} data - Customer data
   * @returns {Promise} API response with created customer
   */
  createCustomer: (data: {
    name: string;
    email: string;
    companyName?: string;
    phone?: string;
    billingAddress?: any;
    currency?: string;
    timezone?: string;
  }) => api.post(API_ENDPOINTS.PAYMENT_GATEWAY.CREATE_CUSTOMER, data),
  

  // User Management
  
  /**
   * Get all users (root admin only)
   * 
   * @returns {Promise} API response with users list
   */
  getUsers: () => api.get(API_ENDPOINTS.ADMIN.GET_USERS),
  
  /**
   * Delete a user (root admin only)
   * 
   * @param {string} id - User ID
   * @returns {Promise} API response
   */
  deleteUser: (id: string) => api.delete(API_ENDPOINTS.ADMIN.DELETE_USER(id)),
  
  /**
   * Invite a user (root admin only)
   * 
   * @param {Object} data - Invitation data
   * @param {string} data.email - User email
   * @param {string} data.role - User role
   * @returns {Promise} API response
   */
  inviteUser: (data: { email: string; role: string }) => 
    api.post(API_ENDPOINTS.ADMIN.INVITE_USER, data),
};

export default api; 